import { Show } from "@/components/atoms/show";
import { Button } from "@/components/ui/button";
import { Card, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Clock, Pencil, Video } from "lucide-react";
import { CollectionFormScreenProps } from "../../types";
import { getCollectionFormStyles } from "../../utils/extract-review-form-styles";

export const WelcomeScreen = ({
  data,
  onChangeStep,
}: CollectionFormScreenProps) => {
  const styles = getCollectionFormStyles(data);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        className={cn(styles.cardPadding, styles.cardSpacing)}
        style={{
          backgroundColor: styles["secondaryColor"],
          color: styles["secondaryTextColor"],
        }}
      >
        {/* Header Section */}
        <div className={cn(styles.cardSpacing)}>
          <CardTitle className="testify-text-center testify-text-xl sm:testify-text-2xl md:testify-text-3xl testify-font-bold">
            {data.welcomePage.title}
          </CardTitle>

          <Show when={!!data.welcomePage.showEstimatedTime}>
            <div className="testify-flex testify-justify-center">
              <div className="testify-flex testify-items-center testify-gap-1.5 testify-text-xs sm:testify-text-sm testify-text-muted-foreground">
                <Clock className="testify-h-3 testify-w-3 sm:testify-h-4 sm:testify-w-4" />
                <span>Takes {data.welcomePage.estimatedMinutes} minutes</span>
              </div>
            </div>
          </Show>
        </div>

        {/* Message Section */}
        <Show
          when={!data.settings.isFrozen}
          fallback={
            <CardDescription
              className="testify-text-center testify-text-sm sm:testify-text-base"
              style={{
                backgroundColor: styles["secondaryColor"],
                color: styles["secondaryTextColor"],
              }}
              dangerouslySetInnerHTML={{ __html: data.labels.pauseMessage }}
            />
          }
        >
          <div className="testify-relative testify-px-3 sm:testify-px-4 testify-py-4 sm:testify-py-6">
            <CardDescription
              style={{
                backgroundColor: styles["secondaryColor"],
                color: styles["secondaryTextColor"],
              }}
              className="testify-text-center testify-text-sm sm:testify-text-base testify-leading-relaxed"
              dangerouslySetInnerHTML={{ __html: data.welcomePage.message }}
            />
          </div>

          {/* Action Buttons */}
          <div className="testify-space-y-2 sm:testify-space-y-3">
            <Show when={data.responsePage.collectionTypes.includes("text")}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Button
                  className="testify-w-full"
                  variant="secondary"
                  onClick={() => onChangeStep("textResponse")}
                  style={{
                    background: styles["primaryColor"],
                    color: styles["primaryTextColor"],
                  }}
                >
                  <div className="testify-flex testify-items-center testify-gap-2 sm:testify-gap-3">
                    <Pencil
                      className="testify-size-4"
                      style={{ color: styles["primaryTextColor"] }}
                    />
                    <div className="testify-text-left">
                      <p className="testify-font-medium testify-text-sm sm:testify-text-base">
                        {data.labels.textButton}
                      </p>
                    </div>
                  </div>
                </Button>
              </motion.div>
            </Show>

            <Show when={data.responsePage.collectionTypes.includes("video")}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  className="testify-w-full"
                  variant="secondary"
                  onClick={() => onChangeStep("videoResponse")}
                  style={{
                    background: styles["primaryColor"],
                    color: styles["primaryTextColor"],
                  }}
                >
                  <div className="testify-flex testify-items-center testify-gap-2 sm:testify-gap-3">
                    <Video
                      className="testify-size-4"
                      style={{ color: styles["primaryTextColor"] }}
                    />
                    <div className="testify-text-left">
                      <p className="testify-font-medium testify-text-sm sm:testify-text-base">
                        {data.labels.videoButton}
                      </p>
                    </div>
                  </div>
                </Button>
              </motion.div>
            </Show>
          </div>
        </Show>
      </Card>
    </motion.div>
  );
};
